<template>
  <vs-popup id="popupAIImageRiskRecommendation" title="이미지 기반 AI 위험요인 추천" :active.sync="showPopup" :width="popupWidth">
    <div>
      <!-- 이미지 업로드 단계 -->
      <div v-if="currentStep === 'upload'" class="upload-container">
        <div class="upload-header">
          <h3 class="upload-title">
            <feather-icon icon="ImageIcon" size="20" class="mr-2" />{{ $t('위험성평가 이미지 업로드') }}</h3>
          <p class="upload-subtitle">{{ $t('작업 현장의 이미지를 업로드하여 AI 기반 위험요인 분석을 시작하세요') }}</p>
        </div>

        <!-- 드래그앤드롭 영역 -->
        <div
          class="drop-zone"
          :class="{ 'drag-over': isDragOver, 'has-image': selectedImage }"
          @drop="handleDrop"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
          @click="triggerFileInput"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="handleFileSelect"
            class="hidden-file-input"
          />

          <!-- 이미지가 선택되지 않은 상태 -->
          <div v-if="!selectedImage" class="drop-zone-content">
            <div class="drop-icon">
              <feather-icon icon="UploadIcon" size="48" />
            </div>
            <h4 class="drop-title">{{ $t('이미지를 드래그하여 업로드하거나 클릭하여 선택하세요') }}</h4>
            <p class="drop-subtitle">{{ $t('JPG, PNG, GIF, WebP 형식 지원 (최대 10MB)') }}</p>
            <vs-button type="border" size="large" class="mt-4">
              <feather-icon icon="FolderIcon" size="16" class="mr-2" />{{ $t('파일 선택') }}</vs-button>
          </div>

          <!-- 이미지가 선택된 상태 -->
          <div v-else class="image-preview">
            <img :src="imagePreviewUrl" alt="선택된 이미지" class="preview-image" />
            <div class="image-overlay">
              <div class="image-info">
                <p class="image-name">{{ selectedImage.name }}</p>
                <p class="image-size">{{ formatFileSize(selectedImage.size) }}</p>
              </div>
              <vs-button
                type="relief"
                color="danger"
                size="small"
                @click.stop="removeImage"
                class="remove-btn"
              >
                <feather-icon icon="XIcon" size="16" />
              </vs-button>
            </div>
          </div>
        </div>

        <!-- 컨텍스트 정보 입력 -->
        <div v-if="selectedImage" class="context-form">
          <h4 class="context-title">{{ $t('작업 정보 입력 (선택사항)') }}</h4>
          <p class="context-subtitle">{{ $t('추가 정보를 입력하면 더 정확한 위험요인 분석이 가능합니다') }}</p>

          <div class="form-grid">
            <div class="form-group">
              <label>{{ $t('사업장명') }}</label>
              <vs-input
                v-model="contextInfo.사업장명"
                placeholder="예: 달지배수펌프장"
                class="w-full"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('공종명') }}</label>
              <vs-input
                v-model="contextInfo.공종명"
                placeholder="예: 환경시설 유지보수"
                class="w-full"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('작업내용') }}</label>
              <vs-input
                v-model="contextInfo.작업내용"
                placeholder="예: 슬러지 제거 작업"
                class="w-full"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('부서명') }}</label>
              <vs-input
                v-model="contextInfo.부서명"
                placeholder="예: 시설관리팀"
                class="w-full"
              />
            </div>
          </div>

          <div class="upload-actions">
            <vs-button
              type="relief"
              @click="resetUpload"
            >
              <feather-icon icon="ArrowLeftIcon" size="16" class="mr-2" />{{ $t('다시 선택') }}</vs-button>
            <vs-button
              type="filled"
              color="primary"
              @click="analyzeImage"
              :loading="loading"
              :disabled="loading"
            >
              <feather-icon icon="ZapIcon" size="16" class="mr-2" />{{ $t('AI 분석 시작') }}</vs-button>
          </div>
        </div>
      </div>

      <!-- 로딩 상태 -->
      <div v-else-if="currentStep === 'loading'" class="loading-container">
        <div class="loading-spinner">
          <div class="pulse-animation">
            <vs-loading type="circles" color="primary"></vs-loading>
          </div>
          <div class="loading-dots">
            <span class="dot dot1"></span>
            <span class="dot dot2"></span>
            <span class="dot dot3"></span>
          </div>
        </div>
        <p class="loading-text animated-text">{{ $t('AI가 이미지를 분석 중입니다...') }}</p>
        <p class="loading-subtext">{{ $t('잠시만 기다려주세요 (약 15초 소요)') }}</p>
        <div class="progress-bar">
          <div class="progress-fill"></div>
          <div class="progress-dots">
            <div class="progress-dot"></div>
            <div class="progress-dot"></div>
            <div class="progress-dot"></div>
            <div class="progress-dot"></div>
          </div>
        </div>
      </div>

      <!-- 분석 결과 표시 -->
      <div v-else-if="currentStep === 'results' && recommendationData && recommendationData.length > 0" class="recommendation-container">
        <!-- PC 레이아웃 -->
        <div class="desktop-layout" v-show="!isMobile">
          <div class="results-header">
            <div class="header-content">
              <h3 class="results-title">
                <feather-icon icon="EyeIcon" size="20" class="mr-2" />{{ $t('이미지 분석 결과') }}</h3>
              <p class="results-subtitle">{{ $t('AI가 분석한 위험요인을 확인하고 선택하세요') }}</p>
            </div>
            <div class="analysis-info">
              <div class="analyzed-image">
                <img :src="imagePreviewUrl" alt="분석된 이미지" class="analyzed-preview" />
              </div>
            </div>
          </div>

          <vs-tabs color="primary" class="recommendation-tabs">
            <vs-tab label="유해위험요인">
              <div class="tab-content">
                <div class="selection-info mb-4">
                  <p class="text-sm text-gray-600">
                    <feather-icon icon="InfoIcon" size="16" class="mr-1" />{{ $t('원하는 위험요인을 선택하세요. 선택된 항목은 위험성평가표에 세부작업명으로 추가됩니다.') }}</p>
                  <p class="text-sm text-blue-600 mt-1">선택된 항목: {{ selectedItems.length }}개</p>
                </div>

                <div class="recommendation-list">
                  <div
                    v-for="(item, index) in recommendationData"
                    :key="`risk-${index}`"
                    class="recommendation-item"
                    :class="{ selected: selectedItems.includes(index) }"
                    @click="toggleSelection(index)"
                  >
                    <div class="item-checkbox" @click.stop>
                      <vs-checkbox :value="selectedItems.includes(index)" @input="handleCheckboxClick(index)" />
                    </div>
                    <div class="item-content">
                      <div class="item-number">{{ index + 1 }}</div>
                      <div class="item-text">{{ item }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </vs-tab>
          </vs-tabs>
        </div>

        <!-- 모바일 레이아웃 -->
        <div class="mobile-layout" v-show="isMobile">
          <div class="mobile-header mobile-sticky-header">
            <div class="header-title">
              <feather-icon icon="EyeIcon" size="18" class="mr-2" />
              <span>{{ $t('이미지 분석 결과') }}</span>
            </div>
            <div class="selection-count">{{ selectedItems.length }}개 선택됨</div>
          </div>

          <div class="mobile-content-wrapper">
            <div class="mobile-image-info">
              <img :src="imagePreviewUrl" alt="분석된 이미지" class="mobile-analyzed-image" />
            </div>

            <div class="mobile-info">
              <p class="info-text">
                <feather-icon icon="InfoIcon" size="14" class="mr-1" />{{ $t('원하는 위험요인을 선택하세요') }}</p>
            </div>

            <div class="mobile-recommendation-list">
              <div
                v-for="(item, index) in recommendationData"
                :key="`mobile-risk-${index}`"
                class="mobile-recommendation-card"
                :class="{ selected: selectedItems.includes(index) }"
                @click="toggleSelection(index)"
              >
                <div class="card-content-only">
                  <p class="card-text">{{ item }}</p>
                  <div class="card-selection-indicator" v-if="selectedItems.includes(index)">
                    <feather-icon icon="CheckIcon" size="16" />
                  </div>
                </div>
              </div>
            </div>

            <div class="mobile-bottom-spacer"></div>
          </div>
        </div>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="currentStep === 'error'" class="error-container">
        <div class="error-icon">
          <feather-icon icon="AlertTriangleIcon" size="48" color="#ff6b6b" />
        </div>
        <h3 class="error-text">{{ $t('분석 중 오류가 발생했습니다') }}</h3>
        <p class="error-subtext">{{ errorMessage }}</p>
        <div class="error-actions">
          <vs-button type="relief" @click="resetToUpload">
            <feather-icon icon="RefreshCwIcon" size="16" class="mr-2" />{{ $t('다시 시도') }}</vs-button>
          <vs-button type="border" @click="closePopup">
            <feather-icon icon="XIcon" size="16" class="mr-2" />{{ $t('닫기') }}</vs-button>
        </div>
            </div>

            <!-- 팝업 하단 버튼 -->
      <div class="popup-bottom-section" v-if="currentStep !== 'upload'">
        <div class="popup-buttons">
          <vs-button type="border" @click="closePopup">{{ $t('취소') }}</vs-button>

          <!-- 다시 분석 버튼 -->
          <vs-button
            v-if="currentStep === 'results' || currentStep === 'error'"
            type="relief"
            @click="resetToUpload"
          >
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />{{ $t('다시 분석') }}</vs-button>

          <!-- 선택 완료 버튼 -->
          <vs-button
            v-if="currentStep === 'results' && recommendationData && recommendationData.length > 0"
            type="filled"
            color="primary"
            @click="confirmSelection"
            :disabled="selectedItems.length === 0"
          >
            <feather-icon icon="CheckIcon" size="16" class="mr-1" />
            선택 완료 ({{ selectedItems.length }}개)
          </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: 'PopupAIImageRiskRecommendation',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    requestData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      showPopup: this.value,
      currentStep: 'upload', // 'upload', 'loading', 'results', 'error'

      // 이미지 관련
      selectedImage: null,
      imagePreviewUrl: null,
      isDragOver: false,

      // 컨텍스트 정보
      contextInfo: {
        사업장명: '',
        공종명: '',
        작업내용: '',
        부서명: ''
      },

      // 분석 결과 관련
      loading: false,
      recommendationData: [],
      selectedItems: [],
      errorMessage: '',

      // 반응형 관련
      windowWidth: window.innerWidth
    }
  },

  computed: {
    popupWidth() {
      return this.windowWidth < 768 ? '95%' : '1200px'
    },

    isMobile() {
      return this.windowWidth < 768
    }
  },

  watch: {
    value(newVal) {
      this.showPopup = newVal
      if (newVal) {
        this.initializePopup()
      }
    },

    showPopup(newVal) {
      this.$emit('input', newVal)
      if (!newVal) {
        this.resetPopup()
      }
    },

    requestData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.contextInfo.사업장명 = newData.사업장명 || ''
          this.contextInfo.공종명 = newData.공종명 || ''
          this.contextInfo.작업내용 = newData.작업내용 || ''
          this.contextInfo.부서명 = newData.부서명 || ''
        }
      },
      immediate: true,
      deep: true
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.cleanupImageUrl()
  },

  methods: {
    initializePopup() {
      console.log('팝업 초기화 시작')
      this.currentStep = 'upload'
      this.selectedImage = null
      this.cleanupImageUrl()
      this.selectedItems = []
      this.recommendationData = []
      this.errorMessage = ''
      this.loading = false
      this.isDragOver = false

      // 파일 입력 초기화
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
          console.log('파일 input 초기화 완료')
        }
      })
    },

    resetPopup() {
      console.log('팝업 리셋 시작')
      this.currentStep = 'upload'
      this.selectedImage = null
      this.cleanupImageUrl()
      this.selectedItems = []
      this.recommendationData = []
      this.errorMessage = ''
      this.loading = false
      this.isDragOver = false

      // 파일 입력 초기화 - 중요: 이것이 없으면 두 번째 업로드가 동작하지 않음
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
          console.log('팝업 리셋 - 파일 input 초기화 완료')
        }
      })
    },

    // 이미지 처리 메서드
    handleDrop(event) {
      event.preventDefault()
      this.isDragOver = false

      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.processFile(files[0])
      }
    },

    handleDragOver(event) {
      event.preventDefault()
      this.isDragOver = true
    },

    handleDragLeave(event) {
      event.preventDefault()
      this.isDragOver = false
    },

    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
      // 같은 파일을 다시 선택할 수 있도록 value 초기화
      // (하지만 즉시 초기화하면 안 되므로 다음 틱에서 실행)
      this.$nextTick(() => {
        if (event.target) {
          event.target.value = ''
        }
      })
    },

    processFile(file) {
      console.log('processFile 호출됨:', file.name, file.size)

      // 파일 유형 검증
      if (!file.type.startsWith('image/')) {
        this.$vs.notify({
          title: '파일 형식 오류',
          text: '이미지 파일만 업로드할 수 있습니다.',
          color: 'danger'
        })
        return
      }

      // 파일 크기 검증 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$vs.notify({
          title: '파일 크기 오류',
          text: '파일 크기는 10MB 이하여야 합니다.',
          color: 'danger'
        })
        return
      }

      this.cleanupImageUrl()
      this.selectedImage = file
      this.imagePreviewUrl = URL.createObjectURL(file)
    },

    removeImage() {
      this.selectedImage = null
      this.cleanupImageUrl()
    },

    cleanupImageUrl() {
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl)
        this.imagePreviewUrl = null
      }
    },

    resetUpload() {
      this.removeImage()
      // 파일 입력 초기화
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    resetToUpload() {
      this.currentStep = 'upload'
      this.errorMessage = ''
      this.recommendationData = []
      this.selectedItems = []
    },

    // API 호출 메서드
    async analyzeImage() {
      if (!this.selectedImage) {
        this.$vs.notify({
          title: '이미지 없음',
          text: '분석할 이미지를 선택해주세요.',
          color: 'warning'
        })
        return
      }

      this.loading = true
      this.currentStep = 'loading'

      try {
        const requestParams = {
          image: this.selectedImage,
          content: this.contextInfo
        }

        const response = await this.$store.dispatch('miras/ANALYZE_IMAGE_RISK', requestParams)

        if (response && response.success && response.data && response.data.유해위험요인_설명) {
          this.recommendationData = response.data.유해위험요인_설명
          this.currentStep = 'results'
          this.selectedItems = []
        } else {
          throw new Error('분석 결과를 받을 수 없습니다.')
        }
      } catch (error) {
        console.error('이미지 분석 오류:', error)
        this.errorMessage = error.message || '이미지 분석 중 오류가 발생했습니다.'
        this.currentStep = 'error'
      } finally {
        this.loading = false
      }
    },

    // 선택 관련 메서드
    toggleSelection(index) {
      const currentIndex = this.selectedItems.indexOf(index)
      if (currentIndex > -1) {
        this.selectedItems.splice(currentIndex, 1)
      } else {
        this.selectedItems.push(index)
      }
    },

    handleCheckboxClick(index) {
      this.toggleSelection(index)
    },

    confirmSelection() {
      if (this.selectedItems.length === 0) {
        this.$vs.notify({
          title: '선택 없음',
          text: '하나 이상의 위험요인을 선택해주세요.',
          color: 'warning'
        })
        return
      }

      const selectedData = this.selectedItems.map(index => this.recommendationData[index])

      this.$emit('selectComplete', {
        items: selectedData,
        indices: this.selectedItems,
        analysisData: {
          image: this.selectedImage,
          context: this.contextInfo,
          fullResponse: this.$store.getters['miras/getImageRiskAnalysis']
        }
      })

      this.closePopup()
    },

    closePopup() {
      this.showPopup = false
    },

    // 유틸리티 메서드
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-container {
  padding: 20px;

  .upload-header {
    text-align: center;
    margin-bottom: 30px;

    .upload-title {
      font-size: 20px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .upload-subtitle {
      font-size: 14px;
      color: #7f8c8d;
      margin: 0;
    }
  }

  .drop-zone {
    border: 2px dashed #ddd;
    border-radius: 12px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #fafafa;
    position: relative;
    margin-bottom: 30px;

    &.drag-over {
      border-color: #007bff;
      background: #f0f8ff;
      transform: scale(1.02);
    }

    &.has-image {
      padding: 0;
      border: none;
      background: transparent;
    }

    .hidden-file-input {
      display: none;
    }

    .drop-zone-content {
      .drop-icon {
        margin-bottom: 20px;
        color: #007bff;
      }

      .drop-title {
        font-size: 16px;
        font-weight: 500;
        color: #2c3e50;
        margin-bottom: 8px;
      }

      .drop-subtitle {
        font-size: 14px;
        color: #7f8c8d;
        margin-bottom: 0;
      }
    }

    .image-preview {
      position: relative;
      border-radius: 12px;
      overflow: hidden;

      .preview-image {
        width: 100%;
        max-height: 300px;
        object-fit: cover;
        display: block;
      }

      .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.7) 100%);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 15px;

        .image-info {
          color: white;

          .image-name {
            font-weight: 500;
            margin: 0 0 5px 0;
            font-size: 14px;
          }

          .image-size {
            margin: 0;
            font-size: 12px;
            opacity: 0.8;
          }
        }

        .remove-btn {
          align-self: flex-end;
        }
      }
    }
  }

  .context-form {
    .context-title {
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 8px;
    }

    .context-subtitle {
      font-size: 14px;
      color: #7f8c8d;
      margin-bottom: 20px;
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 15px;
      margin-bottom: 30px;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }

      .form-group {
        label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #2c3e50;
          margin-bottom: 5px;
        }
      }
    }

    .upload-actions {
      text-align: center;
      display: flex;
      justify-content: center;
      gap: 12px;

      @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        gap: 10px;

        .vs-button {
          width: 100%;
          max-width: 300px;
        }
      }
    }
  }
}

.results-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;

  .header-content {
    flex: 1;

    .results-title {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 5px;
      display: flex;
      align-items: center;
    }

    .results-subtitle {
      font-size: 14px;
      color: #7f8c8d;
      margin: 0;
    }
  }

  .analysis-info {
    .analyzed-image {
      .analyzed-preview {
        width: 120px;
        height: 80px;
        object-fit: cover;
        border-radius: 6px;
        border: 2px solid #e9ecef;
      }
    }
  }
}

.mobile-image-info {
  padding: 15px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;

  .mobile-analyzed-image {
    width: 100%;
    max-width: 200px;
    height: 120px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #e9ecef;
  }
}

// 로딩 컨테이너 - PopupAIRiskRecommendation과 동일
.loading-container {
  text-align: center;
  padding: 60px 20px;

  .loading-spinner {
    margin-bottom: 20px;
    position: relative;
  }

  .pulse-animation {
    animation: pulse 2s infinite;
  }

  .loading-dots {
    display: flex;
    justify-content: center;
    margin: 20px 0;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #007bff;
      margin: 0 4px;
      animation: bounce 1.5s infinite ease-in-out;

      &.dot1 {
        animation-delay: -0.32s;
      }

      &.dot2 {
        animation-delay: -0.16s;
      }

      &.dot3 {
        animation-delay: 0s;
      }
    }
  }

  .loading-text {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;

    &.animated-text {
      animation: fadeInOut 2s infinite;
    }
  }

  .loading-subtext {
    font-size: 14px;
    color: #666;
  }

  .progress-bar {
    width: 100%;
    height: 4px;
    background: #e9ecef;
    border-radius: 2px;
    margin-top: 20px;
    overflow: hidden;
    position: relative;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #007bff, #28a745, #007bff);
      background-size: 200% 100%;
      animation: progressSlide 1.5s infinite linear;
      width: 100%;
      border-radius: 2px;
    }

    .progress-dots {
      position: absolute;
      top: -8px;
      left: 0;
      width: 100%;
      height: 20px;
      display: flex;
      justify-content: space-around;
      align-items: center;

      .progress-dot {
        width: 6px;
        height: 6px;
        background: #007bff;
        border-radius: 50%;
        animation: dotPulse 2s infinite ease-in-out;

        &:nth-child(1) {
          animation-delay: 0s;
        }
        &:nth-child(2) {
          animation-delay: 0.5s;
        }
        &:nth-child(3) {
          animation-delay: 1s;
        }
        &:nth-child(4) {
          animation-delay: 1.5s;
        }
      }
    }
  }
}

.error-container {
  text-align: center;
  padding: 60px 20px;

  .error-icon {
    margin-bottom: 20px;
  }

  .error-text {
    font-size: 18px;
    font-weight: 600;
    color: #e74c3c;
    margin-bottom: 10px;
  }

  .error-subtext {
    font-size: 14px;
    color: #7f8c8d;
    margin-bottom: 30px;
  }

  .error-actions {
    display: flex;
    justify-content: center;
    gap: 12px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: 10px;

      .vs-button {
        width: 100%;
        max-width: 300px;
      }
    }
  }
}

.recommendation-container {
  .recommendation-tabs {
    margin-top: 0;
  }

  .tab-content {
    padding: 20px;
  }

  .selection-info {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    border-left: 4px solid #007bff;
  }

  .recommendation-list {
    max-height: 400px;
    overflow-y: auto;

    .recommendation-item {
      display: flex;
      align-items: center;
      padding: 15px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      margin-bottom: 10px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f8f9fa;
        border-color: #007bff;
      }

      &.selected {
        background: #e7f3ff;
        border-color: #007bff;
      }

      .item-checkbox {
        margin-right: 15px;
      }

      .item-content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 15px;

        .item-number {
          font-weight: 600;
          color: #007bff;
          min-width: 30px;
        }

        .item-text {
          flex: 1;
          line-height: 1.5;
        }
      }
    }
  }
}

.popup-bottom-section {
  padding: 20px;
  border-top: 1px solid #e9ecef;
  background: #fafafa;
  margin-top: 20px;

  .popup-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 12px;
  }
}

// 모바일 레이아웃 스타일
.mobile-layout {
  .mobile-sticky-header {
    position: sticky;
    top: 0;
    background: white;
    padding: 15px 20px;
    border-bottom: 1px solid #e9ecef;
    z-index: 10;

    .header-title {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 5px;
    }

    .selection-count {
      font-size: 12px;
      color: #007bff;
    }
  }

  .mobile-content-wrapper {
    max-height: 60vh;
    overflow-y: auto;
  }

  .mobile-info {
    padding: 15px 20px;
    background: #f8f9fa;

    .info-text {
      font-size: 13px;
      color: #6c757d;
      margin: 0;
      display: flex;
      align-items: center;
    }
  }

  .mobile-recommendation-list {
    padding: 15px 20px;

    .mobile-recommendation-card {
      border: 1px solid #e9ecef;
      border-radius: 8px;
      margin-bottom: 10px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #007bff;
      }

      &.selected {
        border-color: #007bff;
        background: #e7f3ff;
      }

      .card-content-only {
        padding: 15px;
        position: relative;

        .card-text {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          padding-right: 30px;
        }

        .card-selection-indicator {
          position: absolute;
          right: 15px;
          top: 15px;
          color: #007bff;
        }
      }
    }
  }

  .mobile-bottom-spacer {
    height: 80px;
  }
}

// 애니메이션 - PopupAIRiskRecommendation과 동일
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes progressSlide {
  0% {
    background-position: 200% 0;
    transform: translateX(-100%);
  }
  50% {
    background-position: 0% 0;
    transform: translateX(0%);
  }
  100% {
    background-position: -200% 0;
    transform: translateX(100%);
  }
}

@keyframes dotPulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1.2);
  }
}

.animated-text {
  animation: fadeInOut 2s ease-in-out infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 반응형 스타일
@media (max-width: 768px) {
  .upload-container {
    padding: 15px;

    .drop-zone {
      padding: 30px 15px;

      .drop-zone-content {
        .drop-title {
          font-size: 14px;
        }

        .drop-subtitle {
          font-size: 12px;
        }
      }
    }

    .context-form {
      .form-grid {
        gap: 10px;
      }
    }
  }

  .results-header {
    flex-direction: column;
    text-align: center;

    .analysis-info {
      .analyzed-preview {
        width: 100px;
        height: 60px;
      }
    }
  }

  .loading-container {
    padding: 40px 20px;

    .loading-text {
      font-size: 15px;
    }

    .loading-subtext {
      font-size: 13px;
    }
  }

  .error-container {
    padding: 40px 15px;

    .error-text {
      font-size: 16px;
    }

    .error-subtext {
      font-size: 13px;
    }


  }

      .popup-bottom-section {
    padding: 15px;

    .popup-buttons {
      flex-direction: column;
      gap: 10px;

      .vs-button {
        width: 100%;
        max-width: 300px;
      }
    }
  }
}

// PC 환경 팝업 스타일
@media (min-width: 768px) {
  #popupAIImageRiskRecommendation::v-deep {
    .vs-popup {
      min-width: 1000px;
      .vs-popup--content {
        max-height: 90vh;
        overflow-y: auto;
      }
    }
  }
}
</style>
